import { IHome } from "@src/components/Home";
import React, { useMemo, useState, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import Image from "next/image";
import ClientOnly from "@src/components/ClientOnly";
import Floater from "../Floater";
import Footer from "../Footer";
import Vendors from "../Vendors";
import Parts from "../Parts";
import Backstory from "../Backstory";
import MarqueeComp from "../MarqueeComp";
import Quotes from "../Quotes";
import Masonry from "../Masonry";
import Hero from "../Hero";
import AsoEbi from "../AsoEbiLink";
import { useEventParts } from "@src/hooks/useEventParts";
import { useHome } from "@src/hooks/useHome";
import WeddingParty from "../WeddingParty";
import FAQ from "../FAQ";

const sectionNames = [
  "hero",
  "parts",
  "quotes",
  "backstory",
  "faq",
  "weddingParty",
  "masonry",
  "vendors",
];

const HomeCT1: React.FC<IHome> = ({
  slug,
  eventBySlugData,
  eventId,
  eventPartsData,
  backStoryData,
  eventVendorData,
  albumDetailsData,
  heroImageData,
  hasWallet,
  getEventStreamersData,
  liveSchedules,
  eventUuid,
  weddingPartyData,
  inviteResult: initialInviteResult,
}) => {
  const { accessToken, inviteResult, getAsoebiData, isInvitedToEvent } =
    useHome({
      slug,
      eventBySlugData,
      eventId,
      eventPartsData,
      backStoryData,
      eventVendorData,
      albumDetailsData,
      heroImageData,
      hasWallet,
      getEventStreamersData,
      liveSchedules,
      eventUuid,
      weddingPartyData,
      inviteResult: initialInviteResult,
    });
  const { parts } = useEventParts({
    eventPartsData,
    liveSchedules,
    eventTimezone: eventBySlugData?.eventBySlug?.eventTimezone,
    isInvitedToEvent,
  });

  // 1. Extract all valid images from the photobook
  const allImages = useMemo(() => {
    const media = albumDetailsData?.getMedia || [];
    return media
      .filter(
        (img) =>
          img &&
          img.meta &&
          typeof img.meta.s3_url === "string" &&
          img.meta.s3_url.trim() !== "" &&
          img.meta.s3_url.startsWith("http")
      )
      .map((img) => (img && img.meta ? img.meta.s3_url : undefined))
      .filter((url): url is string => typeof url === "string");
  }, [albumDetailsData]);

  // 2. Assign images to sections sequentially, cycling if not enough images
  const sectionImages = useMemo(() => {
    const mapping: Record<string, string> = {};
    const fallbackImage = heroImageData.getHeroImage || "";

    sectionNames.forEach((section, idx) => {
      if (allImages.length > 0) {
        // Assign images in order, cycling through if we run out
        mapping[section] = allImages[idx % allImages.length];
      } else {
        // If no images available, use hero image as fallback
        mapping[section] = fallbackImage;
      }
    });
    return mapping;
  }, [allImages, heroImageData]);

  // 3. Section refs and inView hooks
  const [heroRef, heroInView] = useInView({ threshold: 0.5 });
  const [partsRef, partsInView] = useInView({ threshold: 0.5 });
  const [quotesRef, quotesInView] = useInView({ threshold: 0.5 });
  const [backstoryRef, backstoryInView] = useInView({ threshold: 0.5 });
  const [faqRef, faqInView] = useInView({ threshold: 0.5 });
  const [weddingPartyRef, weddingPartyInView] = useInView({ threshold: 0.5 });
  const [masonryRef, masonryInView] = useInView({ threshold: 0.5 });
  const [vendorsRef, vendorsInView] = useInView({ threshold: 0.5 });

  // 4. Track which section is in view
  const [currentSection, setCurrentSection] = useState("hero");
  useEffect(() => {
    if (heroInView) setCurrentSection("hero");
    else if (partsInView) setCurrentSection("parts");
    else if (quotesInView) setCurrentSection("quotes");
    else if (backstoryInView) setCurrentSection("backstory");
    else if (faqInView) setCurrentSection("faq");
    else if (weddingPartyInView) setCurrentSection("weddingParty");
    else if (masonryInView) setCurrentSection("masonry");
    else if (vendorsInView) setCurrentSection("vendors");
  }, [
    heroInView,
    partsInView,
    quotesInView,
    backstoryInView,
    faqInView,
    weddingPartyInView,
    masonryInView,
    vendorsInView,
  ]);

  return (
    <>
      <ClientOnly>
        <Floater
          accessToken={accessToken}
          eventId={eventId}
          eventUuid={eventUuid}
          slugData={eventBySlugData}
          hasWallet={hasWallet}
          getEventStreamersData={getEventStreamersData}
          liveSchedules={liveSchedules}
          access_control={
            eventBySlugData?.eventBySlug?.event_preference?.access_control ??
            false
          }
          qr_code={inviteResult?.signupByInviteLink?.invitation?.qr_code ?? ""}
          getAsoebiData={getAsoebiData}
          admits={inviteResult?.signupByInviteLink?.invitation?.admits ?? 1}
          invitation={inviteResult?.signupByInviteLink?.invitation}
          isInvitedToEvent={isInvitedToEvent}
        />

        {/* Container for the split layout */}
        <div className="relative overflow-hidden">
          <div className="fixed left-0 top-0 w-1/2 h-screen z-0 hidden lg:block">
            <Image
              src={
                sectionImages[currentSection] ||
                heroImageData.getHeroImage ||
                ""
              }
              alt="section-image"
              layout="fill"
              className="object-cover object-center"
              priority
            />
          </div>

          {/* Scrollable Content Half */}
          <div className="w-full lg:w-1/2 lg:ml-[50%] relative z-10 min-h-screen">
            <section ref={heroRef}>
              <Hero
                heroImageData={heroImageData}
                eventId={eventId}
                slugData={eventBySlugData}
                sectionImage={sectionImages["hero"]}
              />
            </section>
            <section ref={partsRef}>
              <Parts
                liveSchedules={liveSchedules}
                eventId={eventId}
                parts={parts}
                eventPartsData={eventPartsData}
                eventTimezone={eventBySlugData?.eventBySlug?.eventTimezone}
                sectionImage={sectionImages["parts"]}
              />
            </section>
            <section ref={quotesRef}>
              <Quotes sectionImage={sectionImages["quotes"]} />
            </section>
            <section ref={backstoryRef}>
              <Backstory
                eventId={eventId}
                backStoryData={backStoryData}
                sectionImage={sectionImages["backstory"]}
              />
            </section>
            <section ref={faqRef}>
              <FAQ sectionImage={sectionImages["faq"]} />
            </section>
            <section ref={weddingPartyRef}>
              <WeddingParty sectionImage={sectionImages["weddingParty"]} />
            </section>
            <section ref={masonryRef}>
              <Masonry
                eventSlug={slug}
                eventId={eventId}
                albumDetailsData={albumDetailsData}
                sectionImage={sectionImages["masonry"]}
              />
            </section>
            <section ref={vendorsRef}>
              <Vendors
                eventId={eventId}
                eventVendorData={eventVendorData}
                sectionImage={sectionImages["vendors"]}
              />
            </section>
          </div>
        </div>

        {/* Footer - Outside the split layout with higher z-index */}
        <div className="relative z-20">
          <Footer slug={eventBySlugData} />
        </div>
      </ClientOnly>
    </>
  );
};

export default HomeCT1;
